import { ChevronLeftIcon, Plus, TablePropertiesIcon, XSquareIcon } from "lucide-react";
import { useState } from "react";



import { useParams } from "react-router-dom";
import { MoonLoader } from "react-spinners";



import { cn } from "@/lib/utils";



import { useForm } from "@/hooks/server-state/use-form";
import { useEntityModalStore } from "@/hooks/store/use-entity-modal-store";
import { usePropertyModalStore } from "@/hooks/store/use-property-modal-store";



import EntitiesListModal from "@/components/common/modals/entities-list-modal";
import { formInputType } from "@/components/common/modals/property-modal";



import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";



import { createInput } from "./entities-elements";


interface Props {
  setEditorData: React.Dispatch<React.SetStateAction<string>>;
  editorRef: React.RefObject<HTMLDivElement>;
}

const FormEditorSidebar: React.FC<Props> = ({ editorRef }) => {
  const { formId } = useParams<{ formId: string }>();
  const { data: form, isLoading } = useForm(+formId!);

  const [isOpen, setIsOpen] = useState(false);
  const [isEntitiesListModalOpen, setIsEntitiesListModalOpen] = useState(false);

  const { onOpen, setEntityId, setProperty } = usePropertyModalStore();
  const { onOpen: onPropertyOpen, setEntity } = useEntityModalStore();

  const insertFormElement = (element: string) => {
    if (editorRef.current) {
      const selection = window.getSelection();

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const commonAncestor = range.commonAncestorContainer;

        if (!editorRef.current.contains(commonAncestor)) {
          return;
        }

        const fragment = document
          .createRange()
          .createContextualFragment(element);

        range.insertNode(fragment);

        range.setStartAfter(editorRef.current.lastChild!);
        range.collapse(true);

        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  return (
    <>
      <EntitiesListModal
        isOpen={isEntitiesListModalOpen}
        formId={+formId!}
        onClose={() => setIsEntitiesListModalOpen(false)}
      />

      <div
        className={cn(
          "sticky top-4 z-40 border border-slate-300 bg-white pt-5", 
          "flex h-min flex-col"
        )}
      >
        {/* Toggle Button */}
        <Button
          variant="secondary"
          className="absolute right-0 top-10 h-20 translate-x-full rounded-e-none p-0 px-1"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <ChevronLeftIcon
            className={cn("text-white", isOpen ? "rotate-0" : "rotate-180")}
          />
        </Button>

        {/* Sidebar Content Wrapper */}
        <div
          className={cn(
            "flex h-full flex-col overflow-hidden transition-all duration-700",
            isOpen ? "w-[350px]" : "w-0"
          )}
        >
          {/* Scrollable Area */}
          <div
            className="flex-1 overflow-y-auto px-[20px] pb-4"
            style={{ maxHeight: "calc(100vh - 8rem)" }}
          >
            {" "}
            {/* اضافه کردن maxHeight برای محدودیت ارتفاع */}
            {isLoading ? (
              <Loading />
            ) : form?.entities?.length ? (
              <Accordion type="single" collapsible>
                {form?.entities?.map((entity) => (
                  <AccordionItem key={entity.id} value={`entity-${entity.id}`}>
                    <AccordionTrigger className="text-nowrap py-2 text-sm">
                      {entity.previewName}
                    </AccordionTrigger>

                    <AccordionContent>
                      {entity?.properties.map((property) => {
                        const mappedType =
                          formInputType[
                            property.type as keyof typeof formInputType
                          ];

                        return (
                          <Button
                            key={property.id}
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              insertFormElement(
                                createInput({
                                  inputId: property.id,
                                  input: {
                                    input: mappedType.input,
                                    type: mappedType.type,
                                    name: property.previewName
                                  },
                                  defaultValue: property.defaultValue ?? "",
                                  allowedFileExtensions:
                                    property.allowedFileExtensions
                                })
                              );
                            }}
                            className="w-full justify-start text-nowrap !text-xs"
                          >
                            {property.previewName}
                          </Button>
                        );
                      })}

                      <Button
                        variant="ghost"
                        className="my-2 w-full"
                        onClick={() => {
                          setEntityId(entity.id);
                          setProperty(null);
                          onOpen();
                        }}
                      >
                        <Plus />
                      </Button>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <EmptyState />
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200 p-3 flex flex-col gap-y-3">
            <Button
              variant="outline"
              className="w-full gap-x-2"
              onClick={() => setIsEntitiesListModalOpen(true)}
            >
              <TablePropertiesIcon size={16} />
              نمایش جداول
            </Button>
            <Button className="flex items-center gap-x-2 text-sm font-semibold"
                onClick={() => {
                  onPropertyOpen();
                  setEntity(null);
                }}>
                <XSquareIcon size={16} />
                ساخت جدول جدید
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const Loading = () => {
  return (
    <div className="flex size-full items-center justify-center">
      <MoonLoader color="#0099A5" size={50} />
    </div>
  );
};

const EmptyState = () => (
  <div className="flex h-1/2 items-center justify-center">
    <p className="text-slate-600">جدولی یافت نشد</p>
  </div>
);

export default FormEditorSidebar;